// Enums matching backend (can also use string literals)
export type OrderType = "market" | "limit" | "stop_market" | "stop_limit" | "layered" | "cancel_all" | "cancel_bids" | "cancel_asks" | "unknown";
export type OrderSide = "buy" | "sell";
export type AmountType = "percent" | "base" | "quote";
export type MarginMode = "cross" | "isolated";
export type PresetType = "preset" | "tradeset";

// Convert PriceType from type to enum to be usable both as type and value
export enum PriceType {
  ABSOLUTE = "absolute",
  RELATIVE = "relative",
  PERCENT = "percent",
  MARK = "mark",
  INDEX = "index",
  ORACLE = "oracle",
  BEST_BID = "best_bid",
  BEST_ASK = "best_ask"
}
// For backwards compatibility with existing code that uses the type
export type PriceTypeString = keyof typeof PriceType;

// Limit order specific price types (subset of PriceType)
export enum LimitPriceType {
  ABSOLUTE = "ABSOLUTE",
  BEST_BID = "BEST_BID", 
  BEST_ASK = "BEST_ASK",
  RELATIVE = "RELATIVE",
  PERCENT = "PERCENT"
}

// Price reference types for RELATIVE and PERCENT calculations
export enum PriceReference {
  MARK = "MARK",
  INDEX = "INDEX", 
  LAST = "LAST",
  MID = "MID"
}

// Interfaces for nested settings objects
export interface TakeProfitSettings {
  enabled: boolean;
  trigger_price_value?: number | null; // Renamed from price, matches backend
  price_type?: LimitPriceType | null; // Updated to use new price type system
  price_offset?: number | null; // Added for BEST_BID/BEST_ASK/RELATIVE/PERCENT calculations
  price_reference?: PriceReference | null; // Added for RELATIVE/PERCENT calculations
  limit_price_value?: number | null; // Added for stop-limit TP, matches backend
  limit_price_type?: PriceType | null; // Added for stop-limit TP, matches backend
}

export interface StopLossSettings {
  enabled: boolean;
  trigger_price_value?: number | null; // Renamed from price, matches backend
  price_type?: LimitPriceType | null; // Updated to use new price type system
  price_offset?: number | null; // Added for BEST_BID/BEST_ASK/RELATIVE/PERCENT calculations
  price_reference?: PriceReference | null; // Added for RELATIVE/PERCENT calculations
  limit_price_value?: number | null; // Added for stop-limit SL, matches backend
  limit_price_type?: PriceType | null; // Added for stop-limit SL, matches backend
}

export interface LayeredOrderSettings {
  parts: number; // Matches backend
  step: number; // Matches backend
  
  // NEW: Price type support (same as limit orders)
  price_type?: LimitPriceType;
  price_offset?: number;
  price_reference?: PriceReference;
  
  // NEW: Distribution configuration
  distribution_type?: string; // "step_based" | "zone_based"
  center_on_price?: boolean;
  
  // NEW: Zone-based fields (for Phase 3)
  zone_bottom?: number;
  zone_top?: number;
  distribution_strategy?: string; // "even" | "weighted" | "edge_focused"
}

export interface OrderFlags {
  reduce_only: boolean;
  post_only: boolean;
}

// Main Preset interface matching PresetResponse from backend
export interface Preset {
  id: string; // UUID string from backend
  name: string;
  description?: string | null;
  symbol: string;
  order_type: OrderType;
  side: OrderSide;
  amount: number;
  amount_type: AmountType;
  margin_mode: MarginMode; // Should not be optional in response? Check backend PresetResponse
  leverage: number;
  limit_price?: number | null;
  limit_price_type?: LimitPriceType | null; // ADDED: For limit orders
  price_offset?: number | null; // ADDED: Offset for best_bid/ask
  price_reference?: PriceReference | null; // ADDED: Price reference for RELATIVE/PERCENT
  stop_price?: number | null;
  take_profit: TakeProfitSettings; // Uses updated interface
  stop_loss: StopLossSettings; // Uses updated interface
  layered?: LayeredOrderSettings | null; // Renamed from layered_settings to match backend PresetBase/PresetResponse
  flags: OrderFlags;
  webhook_enabled: boolean;
  webhook_id?: string | null;
  use_native_tp_sl?: boolean; // NEW: Toggle between native and advanced TP/SL implementations
  created_at: string; // Keep as string for ISO format
  updated_at: string; // Keep as string for ISO format
  preset_type: PresetType; // Added to distinguish between preSet and tradeSet
}

// TradeSet specific interface to handle multiple linked presets
export interface TradeSetPresetLink {
  preset_id: string; // UUID string from backend
  order: number; // Execution order within the tradeSet
  conditions?: {
    // Optional execution conditions (e.g., execute if previous preset succeeded)
    wait_for_fill?: boolean;
    delay_seconds?: number;
  };
}

export interface TradeSet {
  id: string; // UUID string from backend
  name: string;
  symbol: string;
  description?: string | null;
  presets: Preset[]; // Array of actual Preset objects
  preset_links: TradeSetPresetLink[]; // Array of preset links with order information
  webhook_enabled: boolean;
  webhook_id?: string | null;
  created_at: string;
  updated_at: string;
}

// Interface for the API list response
export interface PresetListResponse {
  items: Preset[];
  total: number;
  page: number;
  size: number;
}

// Interface for the API tradeSet list response
export interface TradeSetListResponse {
  items: TradeSet[];
  total: number;
  page: number;
  size: number;
}

// Base interface for Create/Update requests (optional, helps consistency)
interface PresetBaseRequest {
  name: string;
  description?: string | null;
  symbol: string;
  order_type: OrderType;
  side: OrderSide;
  amount: number;
  amount_type: AmountType;
  margin_mode?: MarginMode; // Keep optional for request flexibility
  leverage: number;
  limit_price?: number | null;
  limit_price_type?: LimitPriceType | null; // ADDED: For limit orders
  price_offset?: number | null; // ADDED: Offset for best_bid/ask
  price_reference?: PriceReference | null; // ADDED: Price reference for RELATIVE/PERCENT
  stop_price?: number | null;
  take_profit?: TakeProfitSettings; // Use updated interface
  stop_loss?: StopLossSettings; // Use updated interface
  layered?: LayeredOrderSettings | null; // Renamed from layered_settings
  flags?: OrderFlags;
  webhook_enabled?: boolean;
  webhook_id?: string | null;
  webhook_secret?: string; // Added field
  allow_override?: boolean; // Added field
  use_native_tp_sl?: boolean; // NEW: Toggle between native and advanced TP/SL implementations
  preset_type: PresetType; // Added type field
}

// Interface for creating a preset (matches backend PresetCreate)
export interface PresetCreateRequest extends PresetBaseRequest {
  preset_type: 'preset'; // Change to literal type
}

// Interface for updating a preset (matches backend PresetUpdate - all fields optional)
export interface PresetUpdateRequest extends Partial<PresetBaseRequest> {}

// Interface for creating a tradeSet
export interface TradeSetCreateRequest {
  name: string;
  symbol: string;
  description?: string | null;
  presets?: TradeSetPresetLink[];
  webhook_enabled?: boolean;
  webhook_id?: string | null;
  webhook_secret?: string; // Added field
  allow_override?: boolean; // Added field to match PresetBaseRequest
  use_native_tp_sl?: boolean; // NEW: Toggle between native and advanced TP/SL implementations
  preset_type: 'tradeset';
}

// Interface for updating a tradeSet
export interface TradeSetUpdateRequest extends Partial<TradeSetCreateRequest> {
  // preset_type should likely not be updatable, or handled specifically
}

// Interface for placing an order directly from the form data
export interface OrderPlacementRequest {
  symbol: string;
  order_type: OrderType;
  side: OrderSide;
  amount: number;
  amount_type: AmountType;
  margin_mode: MarginMode;
  leverage: number;
  limit_price?: number | null;
  limit_price_type?: LimitPriceType | null; // ADDED: For limit orders
  price_offset?: number | null; // ADDED: Offset for best_bid/ask
  price_reference?: PriceReference | null; // ADDED: Price reference for RELATIVE/PERCENT
  stop_price?: number | null;
  take_profit?: TakeProfitSettings; // Send the whole object
  stop_loss?: StopLossSettings; // Send the whole object
  flags?: OrderFlags; // Send the whole object
  layered?: LayeredOrderSettings | null; // Renamed field here too for consistency
}